import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import PhoneIcon from "@mui/icons-material/PhoneIphoneOutlined";
import ModalComponent from "./Modal";
import ErrorMessage from "../ErrorMessage";
import useUpdateContactInfo from "../../hooks/useUpdateContactInfo";
import { parseBackendErrorMessage } from "../../utils/parseBackendError";
import { isValidEmail, isValidPhone } from "../../utils/userValidators";

const UpdateUserInfoModal = ({ open, onClose, user, onSuccess, onError }) => {
  const [newEmail, setNewEmail] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [showDelayMessage, setShowDelayMessage] = useState(false);

  const { updateContactInfo, loading } = useUpdateContactInfo();

  useEffect(() => {
    if (open) {
      setNewEmail(user.email || "");
      if (user.phone_number?.startsWith("+57")) {
        setNewPhoneNumber(user.phone_number.slice(3));
      } else {
        setNewPhoneNumber(user.phone_number || "");
      }
    }
  }, [user, open]);

  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowDelayMessage(true), 10000);
    } else {
      setShowDelayMessage(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  // Función para manejar el envío del formulario y la actualización de la información del usuario
  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedEmail = newEmail.trim();
    const trimmedPhone = newPhoneNumber.trim();

    const formattedPhone = trimmedPhone.length > 0 ? `+57${trimmedPhone}` : null;

    const errors = [];

    // Validaciones
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      errors.push("El correo no tiene un formato válido.");
    }

    if (trimmedPhone && !isValidPhone(trimmedPhone)) {
      errors.push("El teléfono debe tener 10 digitos.");
    }

    if (errors.length > 0) {
      onError?.({ message: errors.join(" ") });
      return;
    }

    const emailChanged = trimmedEmail && trimmedEmail !== user.email;
    const phoneChanged = formattedPhone && formattedPhone !== user.phone_number;

    if (!emailChanged && !phoneChanged) {
      onClose();
      return;
    }

    const payload = {};
    if (emailChanged) payload.email = trimmedEmail;
    if (phoneChanged) payload.phone_number = formattedPhone;

    const response = await updateContactInfo(user.id, payload);

    if (response?.error?.message) {
      const msg = parseBackendErrorMessage(response.error.message);
      onError?.({ message: msg });
      return;
    }

    onSuccess?.();
    onClose();
  };

  return (
    <ModalComponent open={open} handleClose={onClose} width="480px">
      <ErrorMessage title={<div className="text-2xl font-semibold text-gray-800 pb-2 border-b border-gray-200">Actualizar Información</div>}>
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Email */}
          <div>
            <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-2">
              Correo electrónico
            </label>
            <div className="relative rounded-md shadow-sm">
              <input
                maxLength={128}
                type="email"
                id="newEmail"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="nuevo@correo.com"
                className="block w-full px-4 py-3 rounded-lg border border-gray-300 
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                disabled={loading}
              />
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <EmailIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Teléfono */}
          <div className="relative rounded-md shadow-sm">
            {/* Prefijo +57 */}
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-600 text-sm pointer-events-none">+57</span>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={newPhoneNumber}
              onChange={(e) => {
                const onlyNumbers = e.target.value.replace(/\D/g, "").slice(0, 10);
                setNewPhoneNumber(onlyNumbers);
              }}
              placeholder="3123456789"
              className="pl-14 block w-full px-4 py-3 rounded-lg border border-gray-300 
                focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
              disabled={loading}
            />
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <PhoneIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 border-t border-gray-100 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-lg bg-mainYellow hover:bg-mainHover text-black font-medium transition-colors 
                disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Actualizando...
                </div>
              ) : (
                "Actualizar"
              )}
            </button>
          </div>

          {showDelayMessage && <div className="text-sm text-yellow-600 mt-2">Esto puede tardar unos segundos más de lo normal...</div>}
        </form>
      </ErrorMessage>
    </ModalComponent>
  );
};

export default UpdateUserInfoModal;
